"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildMyChartLoginUrl = exports.getProviderIdForOpenScheduling = exports.getExternalIdForOpenScheduling = exports.getExternalSystemProviderIdForOpenScheduling = exports.getVisitTypesFromProviderForOpenScheduling = exports.buildMyChartOpenSchedulingWidgetUrl = exports.convertOverrides = void 0;
const utilities_1 = require("../../utilities");
const types_1 = require("./types");
function convertOverrides(overrides) {
    return {
        loginPath: overrides.login_path,
        loginUrl: overrides.login_url,
        embeddedPath: overrides.embedded_path,
        apiKeyParameterName: overrides.api_key_parameter_name,
        departmentParameterName: overrides.department_parameter_name,
        providerIdParameterName: overrides.provider_id_parameter_name,
        visitTypeOverride: overrides.visit_type_overrides,
        visitTypeParameterName: overrides.visit_type_parameter_name,
        widgetIdParameterName: overrides.widget_id_parameter_name
    };
}
exports.convertOverrides = convertOverrides;
/**
 * Builds the URL for the MyChart Open Scheduling Widget.
 *
 * @param options - The options to configure the URL.
 * @param options.url - The base URL for the MyChart Open Scheduling Widget (required).
 * @param options.apiKey - The API key for authorization (optional).
 * @param options.widgetId - The widget id for the widget (optional).
 * @param options.department - The department identifier (optional).
 * @param options.visitTypes - An array of visit types.
 * @param options.providerId - The provider id.
 * @param options.overrides - Optional overrides for parameter names and paths.
 * @param options.overrides.embeddedPath - Override for the embedded path (default: 'openscheduling/embedded').
 * @param options.overrides.apiKeyParameterName - Override for the API key parameter name (default: 'apiKey').
 * @param options.overrides.widgetIdParameterName - Override for the widget ID parameter name (default: 'widgetId').
 * @param options.overrides.departmentParameterName - Override for the department parameter name (default: 'department').
 * @param options.overrides.visitTypeParameterName - Override for the visit type parameter name (default: 'visitType').
 * @param options.overrides.providerIdParameterName - Override for the provider ID parameter name (default: 'providerId').
 *
 * @returns The constructed URL as a string.
 */
function buildMyChartOpenSchedulingWidgetUrl(options) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const embeddedPath = (_b = (_a = options.overrides) === null || _a === void 0 ? void 0 : _a.embeddedPath) !== null && _b !== void 0 ? _b : 'openscheduling/embedded';
    const output = new URL(embeddedPath, (0, utilities_1.normalizeUrl)(options.url));
    const apiKeyParameterName = (_d = (_c = options.overrides) === null || _c === void 0 ? void 0 : _c.apiKeyParameterName) !== null && _d !== void 0 ? _d : 'apiKey';
    if (options.apiKey) {
        output.searchParams.set(apiKeyParameterName, options.apiKey);
    }
    const widgetIdParameterName = (_f = (_e = options.overrides) === null || _e === void 0 ? void 0 : _e.widgetIdParameterName) !== null && _f !== void 0 ? _f : 'widgetId';
    if (options.widgetId) {
        output.searchParams.set(widgetIdParameterName, options.widgetId);
    }
    if (options.department) {
        const departmentParameterName = (_h = (_g = options.overrides) === null || _g === void 0 ? void 0 : _g.departmentParameterName) !== null && _h !== void 0 ? _h : 'department';
        output.searchParams.set(departmentParameterName, (_j = options.department) !== null && _j !== void 0 ? _j : '');
    }
    for (const visitType of options.visitTypes) {
        const visitTypeParameterName = (_l = (_k = options.overrides) === null || _k === void 0 ? void 0 : _k.visitTypeParameterName) !== null && _l !== void 0 ? _l : 'visitType';
        const values = output.searchParams.getAll(visitTypeParameterName);
        if (!values.includes(visitType)) {
            output.searchParams.append(visitTypeParameterName, visitType);
        }
    }
    const providerIdParameterName = (_o = (_m = options.overrides) === null || _m === void 0 ? void 0 : _m.providerIdParameterName) !== null && _o !== void 0 ? _o : 'providerId';
    output.searchParams.set(providerIdParameterName, options.providerId.toString());
    return output.toString();
}
exports.buildMyChartOpenSchedulingWidgetUrl = buildMyChartOpenSchedulingWidgetUrl;
/**
 * Retrieves the visit types from a provider's appointment EHR purposes.
 *
 * This function extracts the visit types that are marked as 'direct_book' from the provider's
 * appointment EHR purposes. If no visit types are found, it falls back to the default visit type
 * specified in the open scheduling configuration.
 *
 * @param {MyChartOpenSchedulingConfig} openSchedulingConfig - The configuration for open scheduling, which may contain a default visit type.
 * @param {ProviderMatchProviderV9} provider - The provider object containing appointment EHR purposes.
 * @returns {string[]} An array of visit type ids.
 */
function getVisitTypesFromProviderForOpenScheduling(openSchedulingConfig, provider) {
    var _a, _b, _c;
    if ((_a = openSchedulingConfig.overrides) === null || _a === void 0 ? void 0 : _a.visit_type_overrides) {
        if (((_b = openSchedulingConfig.overrides) === null || _b === void 0 ? void 0 : _b.visit_type_overrides.length) > 0) {
            return [...openSchedulingConfig.overrides.visit_type_overrides];
        }
    }
    const visitTypes = [];
    if (provider.appointment_ehr_purposes) {
        for (const ehr_purposes of provider.appointment_ehr_purposes) {
            if (ehr_purposes.ehr_data) {
                for (const value of ehr_purposes.ehr_data) {
                    if (((_c = value === null || value === void 0 ? void 0 : value.visibilities) === null || _c === void 0 ? void 0 : _c.consumer) === 'direct_book') {
                        if (value.book_as_type_id) {
                            visitTypes.push(value.book_as_type_id);
                        }
                    }
                }
            }
        }
    }
    if (visitTypes.length === 0) {
        if (openSchedulingConfig.default_visit_type) {
            visitTypes.push(openSchedulingConfig.default_visit_type);
        }
    }
    return visitTypes;
}
exports.getVisitTypesFromProviderForOpenScheduling = getVisitTypesFromProviderForOpenScheduling;
/**
 * Retrieves the provider id from the external systems associated with a provider
 * for open scheduling, specifically looking for the `epic` system type.
 *
 * @param provider - The provider object containing external systems information.
 * @returns The provider id from the `epic` external system, or null if not found.
 */
function getExternalSystemProviderIdForOpenScheduling(provider) {
    if (!provider.external_systems) {
        return null;
    }
    const epicExternalSystem = provider.external_systems.find((externalSystem) => externalSystem.system_type === types_1.ExternalSystemTypes.Epic);
    if (epicExternalSystem) {
        return epicExternalSystem.provider_id;
    }
    return null;
}
exports.getExternalSystemProviderIdForOpenScheduling = getExternalSystemProviderIdForOpenScheduling;
/**
 * Retrieves the external id for open scheduling from a provider object.
 *
 * This function extracts and validates the external id from the provider's
 * `external_id` property. It ensures that the external id is present and
 * that it has the correct suffix for Epic Open Scheduling (`epic_osw`).
 *
 * @param {ProviderMatchProviderV9} provider - The provider object containing the external id.
 * @returns {string | null} The external id if valid, otherwise `null`.
 */
function getExternalIdForOpenScheduling(provider) {
    if (!provider.external_id) {
        return null;
    }
    const [externalId, externalIdSuffix] = provider.external_id.split('@');
    if (!externalId) {
        return null;
    }
    if (externalIdSuffix !== types_1.ExternalIdSuffixes.EpicOSW) {
        return null;
    }
    return externalId;
}
exports.getExternalIdForOpenScheduling = getExternalIdForOpenScheduling;
/**
 * Retrieves the provider identifier for open scheduling.
 *
 * This function attempts to get the provider id from an external system first.
 * If the external system provider id is not available, it falls back to the
 * provider's external id.
 *
 * @param {ProviderMatchProviderV9} provider - The provider object containing
 * the necessary information to retrieve the id.
 * @returns {string} The provider id for open scheduling.
 */
function getProviderIdForOpenScheduling(provider) {
    const externalSystemProviderId = getExternalSystemProviderIdForOpenScheduling(provider);
    if (externalSystemProviderId) {
        return externalSystemProviderId;
    }
    return getExternalIdForOpenScheduling(provider);
}
exports.getProviderIdForOpenScheduling = getProviderIdForOpenScheduling;
/**
 * Constructs the MyChart login URL based on the provided configuration.
 *
 * @param myChartConfig - The configuration object for MyChart open scheduling.
 *   This object must include all required properties defined in `MyChartOpenSchedulingConfig`.
 *   - `url`: The base URL for MyChart.
 *   - `overrides`: Optional overrides for the default paths.
 *
 * @returns The complete login URL as a string.
 */
function buildMyChartLoginUrl(myChartConfig) {
    var _a, _b, _c;
    if ((_a = myChartConfig.overrides) === null || _a === void 0 ? void 0 : _a.login_url) {
        return myChartConfig.overrides.login_url;
    }
    const loginPath = (_c = (_b = myChartConfig.overrides) === null || _b === void 0 ? void 0 : _b.login_path) !== null && _c !== void 0 ? _c : 'Authentication/Login';
    return new URL(loginPath, (0, utilities_1.normalizeUrl)(myChartConfig.url)).toString();
}
exports.buildMyChartLoginUrl = buildMyChartLoginUrl;
//# sourceMappingURL=utilities.js.map